"use client";

import { PiPencil } from "react-icons/pi";
import { z } from "zod";

import { Button } from "@/components/button/button";
import Form from "@/components/form/form";
import Input from "@/components/input/input";
import { toast } from "@/components/toast/use-toast";
import { getYoutubeVideoId, isYoutubeLink } from "@/forms/youtube.form";
import { useYoutubeBlogPostMutation } from "@/funcs/query/blog.query";
import { useForm } from "@/hooks/use-form";

const youtubeToBlogFormSchema = z.object({
  youtubeLink: z.string().refine(value => isYoutubeLink(value), { message: "Please enter a valid YouTube link." }),
});

export default function YoutubeToBlogForm() {
  const { mutate: createBlogPost, data: blogPost, isPending: isCreatingPost } = useYoutubeBlogPostMutation();

  const form = useForm({
    schema: youtubeToBlogFormSchema,
    defaultValues: {
      youtubeLink: "",
    },
    onValid: data => {
      const id = getYoutubeVideoId(data.youtubeLink);

      if (!id) {
        return toast({
          title: "Getting YouTube Video ID failed, please check that your link is correct!",
          variant: "destructive",
        });
      }

      createBlogPost(id);
    },
  });

  async function handleCopyToClipboard() {
    if (!blogPost) {
      return;
    }

    const text = `${blogPost.title} ${Object.entries(blogPost.paragraphs).map(p => `\n\n${p[0]}:\n ${p[1]}`)}`;

    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied to clipboard!",
        variant: "success",
      });
    } catch (error) {
      console.error("Failed to copy to clipboard!", error);
      toast({
        title: "Failed to copy to clipboard!",
        variant: "destructive",
      });
    }
  }

  return (
    <div className="grid grid-cols-1 gap-y-10">
      <Form {...form} className="w-full max-w-screen-md place-self-center">
        <Form.Field
          control={form.control}
          name="youtubeLink"
          render={field => <Input {...field} placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ" />}
        />
        <Button type="submit" className="mt-4" isLoading={isCreatingPost}>
          <PiPencil className="w-5 h-5" />
          Create Post
        </Button>
      </Form>
      {blogPost && (
        <>
          <hr className="w-full max-w-screen-lg mx-auto" />
          <section className="max-w-screen-lg grid gap-y-5 mx-auto">
            <h2 className="">{blogPost.title}</h2>
            <Button type="button" variant="outline" className="w-fit mx-auto" onClick={handleCopyToClipboard}>
              Copy To Clipboard
            </Button>
            {Object.entries(blogPost.paragraphs).map(([key, value]) => (
              <div key={key}>
                <h3>{key}:</h3>
                <p>{value}</p>
              </div>
            ))}
          </section>
        </>
      )}
    </div>
  );
}
